import type {
  AlignmentValue,
  GravityValue,
  CropValue,
  RolfsImageOptions,
  RolfsImageTransformation,
} from '@packages/media/types/rolfs-image.ts'
import { ROLFS_IMAGE_URL } from '@packages/media/constants/urls.ts'
import { parseDpr } from '@packages/media/utils/images.js'
import { OG_IMAGE_WIDTH, OG_IMAGE_HEIGHT } from '@packages/media/constants/media.ts'

/**
 * Backend api responses may still contain legacy res.cloudinary.com URLs.
 * Convert them to the rolfs image service, keeping any transformation segments.
 */
const CLOUDINARY_UPLOAD_PATH = '/image/upload/'
const convertLegacyCloudinaryUrl = (url: string): string => {
  if (!url.includes('res.cloudinary.com')) {
    return url
  }
  const uploadIndex = url.indexOf(CLOUDINARY_UPLOAD_PATH)
  if (uploadIndex === -1) {
    return url
  }
  const afterUpload = url.slice(uploadIndex + CLOUDINARY_UPLOAD_PATH.length)
  const segments = afterUpload.split('/')
  let i = 0
  while (i < segments.length && /^[a-z0-9_,.:]+$/.test(segments[i]) && segments[i].includes('_')) {
    i++
  }
  const transformation = segments.slice(0, i).join('/')
  const publicId = segments.slice(i).join('/')
  const pathWithSource = publicId.startsWith('source/') ? publicId : `source/${publicId}`
  return transformation ? `${ROLFS_IMAGE_URL}/${transformation}/${pathWithSource}` : `${ROLFS_IMAGE_URL}/${pathWithSource}`
}

const buildSquareCropTransformation = (baseTransformation: string): string => {
  return `${baseTransformation},ar_1:1,c_fill`
}

const buildWatermarkTransformation = (
  baseTransformation: string,
  watermark: string,
  watermarkWidth: number
): string => {
  return `c_fill,w_${OG_IMAGE_WIDTH},h_${OG_IMAGE_HEIGHT}${baseTransformation ? `,${baseTransformation}` : ''},l_wm:${watermark},g_north_west,x_60,y_60,w_${watermarkWidth}`
}

const buildCustomTransformation = (baseTransformation: string, custom: string): string => {
  return `${baseTransformation},${custom}`
}

const buildTransformationString = (
  forceExtension: string | false,
  transformation: RolfsImageTransformation
): string => {
  const baseTransformation = forceExtension ? `f_${forceExtension},q_auto` : 'f_auto,q_auto'

  if (transformation.squareCrop) {
    return buildSquareCropTransformation(baseTransformation)
  }

  if (transformation.watermark) {
    const watermarkWidth = transformation.watermarkWidth ?? 240
    return buildWatermarkTransformation(baseTransformation, transformation.watermark, watermarkWidth)
  }

  if (transformation.custom) {
    return buildCustomTransformation(baseTransformation, transformation.custom)
  }

  return baseTransformation
}

const formatRolfsImagePath = (
  publicId: string,
  opt: RolfsImageOptions = {}
): string => {
  if (!publicId) {
    return ''
  }

  if (publicId.includes('res.cloudinary.com')) {
    return convertLegacyCloudinaryUrl(publicId)
  }

  // If publicId is already a full URL (rolfs image service or other), return as-is
  if (publicId.startsWith('https://')) {
    return publicId
  }

  if (!publicId.startsWith('source/')) {
    publicId = `source/${publicId.replace(/^source+/, '').replace(/^\/+/, '')}`
  }

  publicId = publicId.replaceAll(' ', '%20')

  const { transformation = {}, forceExtension = 'webp', transform = true } = opt

  if (!transform) {
    return `${ROLFS_IMAGE_URL}/${publicId}`
  }

  const transformationStr = buildTransformationString(forceExtension, transformation)

  return `${ROLFS_IMAGE_URL}/${transformationStr}/${publicId}`
}

const repairImageServiceUrl = (url: string): string => {
  const brokenPrefix = `${ROLFS_IMAGE_URL}/source/`

  if (!url.startsWith(brokenPrefix)) {
    return url
  }

  const segments = url.slice(brokenPrefix.length).split('/')
  const transformationSegments = segments.filter(segment => segment.includes(','))
  const publicId = segments
    .filter(segment => segment && !segment.includes(','))
    .join('/')
    .replace(/\.[^/.]+$/, '')

  if (!transformationSegments.length || !publicId) {
    return url
  }

  return `${ROLFS_IMAGE_URL}/${transformationSegments.join('/')}/source/${publicId}`
}

const sanitizeImageServiceHtml = (html: string): string => {
  if (!html) {
    return html
  }

  const escapedBase = ROLFS_IMAGE_URL.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const brokenUrlPattern = new RegExp(`${escapedBase}/source/[^\\s"'<>\\\\)]+`, 'g')

  return html.replace(brokenUrlPattern, match => repairImageServiceUrl(match))
}

const formatRolfsVideoPath = (publicId: string): string => {
  if (publicId.includes('res.cloudinary.com')) {
    return convertLegacyCloudinaryUrl(publicId)
  }

  if (publicId.startsWith('https://')) {
    return publicId
  }

  if (!publicId.startsWith('source/')) {
    publicId = `source/${publicId.replace(/^source+/, '').replace(/^\/+/, '')}`
  }
  return `${ROLFS_IMAGE_URL}/${publicId}`
}

/**
 * Switch case for alignment values used in gravity property
 * The rolfs image service mirrors Cloudinary's gravity naming
 * @see https://cloudinary.com/documentation/resizing_and_cropping#control_gravity
 */
const alignToGravity = (gravity: AlignmentValue): GravityValue => {
  const gravityMap: Record<string, GravityValue> = {
    top: 'north',
    bottom: 'south',
    left: 'west',
    right: 'east',
  }

  return gravityMap[gravity] ?? (gravity as GravityValue)
}

const buildGravityTransformation = (gravity?: AlignmentValue, crop: CropValue = 'fill'): string => {
  const cropTransform = `c_${crop}`

  return gravity
    ? `g_${alignToGravity(gravity)},${cropTransform}`
    : cropTransform
}

const buildDprTransformation = (dpr?: string): string => {
  return `dpr_${dpr ?? parseDpr()}`
}

const buildPartialSizeTransformation = (
  w?: number,
  h?: number,
  gravity?: AlignmentValue,
  dpr?: string,
  crop?: CropValue
): string => {
  const parts: string[] = [
    buildGravityTransformation(gravity, crop),
    buildDprTransformation(dpr),
  ]

  if (w) {
    parts.push(`w_${w}`)
  }

  if (h) {
    parts.push(`h_${h}`)
  }

  return parts.join(',')
}

const buildFullSizeTransformation = (
  w: number,
  h: number,
  gravity?: AlignmentValue,
  dpr?: string,
  crop?: CropValue
): string => {
  return [
    `h_${h}`,
    `w_${w}`,
    buildGravityTransformation(gravity, crop),
    buildDprTransformation(dpr),
  ].join(',')
}

// gravity = 'auto' = AI "pick the most interesting"
const formatRolfsImageSize = (
  w?: number,
  h?: number,
  gravity?: AlignmentValue,
  dpr?: string,
  crop?: CropValue
): string => {
  if (w && h) {
    return buildFullSizeTransformation(w, h, gravity, dpr, crop)
  }

  return buildPartialSizeTransformation(w, h, gravity, dpr, crop)
}

const formatRolfsImageThumbSize = (gravity?: AlignmentValue): string => {
  const gravityTransformation = gravity ? `c_fill,g_${alignToGravity(gravity)}` : 'c_limit'

  return [
    'h_30',
    'e_blur:100',
    gravityTransformation,
  ].join(',')
}

export {
  formatRolfsImagePath,
  repairImageServiceUrl,
  sanitizeImageServiceHtml,
  formatRolfsVideoPath,
  formatRolfsImageSize,
  formatRolfsImageThumbSize,
}
